import { useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useIntl } from '@leagueplatform/locales';
import type {
  EnvironmentKey,
  EntityDetailSchema,
  ConfigEntityDetail,
  DomainEntity,
} from '@web-config-app/core';
import { EntityStatusValue } from '@web-config-app/core';
import { captureError } from '@leagueplatform/observability';
import { isProduction } from '@leagueplatform/app-environment';
import { useLoadEntityRelationshipsSchemas } from '../use-load-entity-relationships-schemas/use-load-entity-relationships-schemas';
import { useFindConfigEntityByDomainId } from '../use-find-config-entity-by-domain-id/use-find-config-entity-by-domain-id';
import { useConfigEntityGetByIdQuery } from '../use-config-entity-get-by-id-query/use-config-entity-get-by-id-query';
import { useConfigEntity } from '../use-config-entity/use-config-entity';
import { useConfigApp } from '../use-config-app/use-config-app';
import { useAbortController } from '../use-abort-controller/use-abort-controller';
import { useEnvironmentFetch } from '../use-environment-fetch/use-environment-fetch';
import { isConfigEntityDetail } from '../../utilities/is-config-entity-detail/is-config-entity-detail';
import {
  getAllReferencedEntities,
  type EntityWithDependencies,
  type DiscoveryFailure,
} from '../../utilities/get-all-referenced-entities/get-all-referenced-entities';
import type { BulkActionReportingStore } from '../../bulk-action-reporting/bulk-action-reporting.store';
import { useUserInfo } from '../use-user-info/use-user-info';
import type { DeepPublishTreeEntry } from '../use-deep-publish/use-deep-publish';
import { getEntityMetadata } from '../use-deep-publish/discover-deep-publish-tree';
import { executeDeepPublishLoop } from '../use-deep-publish/execute-deep-publish';

export interface BulkPublishRootTree {
  rootEntity: ConfigEntityDetail;
  treeEntries: DeepPublishTreeEntry[];
  discoveryFailures: DiscoveryFailure[];
}

export interface UseBulkDeepPublishProps {
  domainEntity: DomainEntity;
  environment: EnvironmentKey;
  bulkActionReportingStore: BulkActionReportingStore;
  getEntityDetailPath?: (
    entityType: string,
    instanceId: string,
  ) => string | undefined;
}

export interface UseBulkDeepPublishResult {
  /** Per-root tree entries for modal (preserves selected order) */
  rootTrees: BulkPublishRootTree[];
  /** Deduplicated count across all trees */
  totalEntityCount: number;
  /** Drafts needing publish */
  totalDraftCount: number;
  /** Shared deps found across trees */
  deduplicatedCount: number;
  /** Whether any root has discovery failures */
  hasDiscoveryFailures: boolean;
  /** Whether tree discovery is in progress */
  isDiscovering: boolean;
  /** Live discovery progress count */
  discoveredSoFar: number;
  /** Whether tree discovery encountered an error */
  isDiscoveryError: boolean;
  /** Error message from tree discovery */
  discoveryErrorMessage?: string;
  /** Whether publish execution is in progress */
  isExecuting: boolean;
  /** Discover the dependency trees for the given selected entities */
  discoverTrees: (roots: ConfigEntityDetail[]) => Promise<void>;
  /** Execute bulk deep publish on the discovered trees */
  executeBulkDeepPublish: () => Promise<void>;
  /** Cancel the in-progress publish operation */
  cancelPublish: () => void;
  /** Reset the hook state */
  reset: () => void;
}

export const useBulkDeepPublish = ({
  domainEntity,
  environment,
  bulkActionReportingStore,
  getEntityDetailPath,
}: UseBulkDeepPublishProps): UseBulkDeepPublishResult => {
  const { formatMessage } = useIntl();
  const [rootTrees, setRootTrees] = useState<BulkPublishRootTree[]>([]);
  const [totalEntityCount, setTotalEntityCount] = useState(0);
  const [totalDraftCount, setTotalDraftCount] = useState(0);
  const [deduplicatedCount, setDeduplicatedCount] = useState(0);
  const [hasDiscoveryFailures, setHasDiscoveryFailures] = useState(false);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [discoveredSoFar, setDiscoveredSoFar] = useState(0);
  const [isDiscoveryError, setIsDiscoveryError] = useState(false);
  const [discoveryErrorMessage, setDiscoveryErrorMessage] = useState<
    string | undefined
  >();
  const [isExecuting, setIsExecuting] = useState(false);

  const { createAbortController, abort } = useAbortController();
  const { userInfo } = useUserInfo();
  const queryClient = useQueryClient();

  const configEntity = useConfigEntity({ domainEntity });
  const { configEntity: configEntityConfig } = useConfigApp();

  const loadEntityRelationshipsSchemas = useLoadEntityRelationshipsSchemas();
  const findEntityOnSourceByDomainEntityId = useFindConfigEntityByDomainId({
    environment,
  });

  const fetchEntityOnSourceById = useConfigEntityGetByIdQuery({
    sourceEnvironment: environment,
    domainEntityType: domainEntity.type,
    includeReferences: true,
  });

  const environmentFetch = useEnvironmentFetch(
    environment,
    configEntity.domainEntity.apiUrl,
  );

  // Unified entity map across all roots for deduplication
  const unifiedEntitiesMapRef = useRef<Map<string, EntityWithDependencies>>(
    new Map(),
  );
  // Unified schema map across all roots
  const unifiedSchemasMapRef = useRef<Map<string, EntityDetailSchema>>(
    new Map(),
  );
  // Track failed entity IDs for retry
  const failedEntityIdsRef = useRef<Set<string>>(new Set());

  const reset = useCallback(() => {
    setRootTrees([]);
    setTotalEntityCount(0);
    setTotalDraftCount(0);
    setDeduplicatedCount(0);
    setHasDiscoveryFailures(false);
    setIsDiscovering(false);
    setDiscoveredSoFar(0);
    setIsDiscoveryError(false);
    setDiscoveryErrorMessage(undefined);
    setIsExecuting(false);
    unifiedEntitiesMapRef.current = new Map();
    unifiedSchemasMapRef.current = new Map();
    failedEntityIdsRef.current = new Set();
  }, []);

  const discoverTrees = useCallback(
    async (roots: ConfigEntityDetail[]) => {
      setIsDiscovering(true);
      setIsDiscoveryError(false);
      setDiscoveryErrorMessage(undefined);
      setDiscoveredSoFar(0);
      setRootTrees([]);

      try {
        const unifiedMap = new Map<string, EntityWithDependencies>();
        const unifiedSchemas = new Map<string, EntityDetailSchema>();
        const perRootTrees: BulkPublishRootTree[] = [];
        let totalDiscoveredEntities = 0;
        let anyFailures = false;

        // Track entity IDs already claimed by an earlier root to mark
        // later occurrences as "already included"
        const claimedEntityIds = new Set<string>();

        // Discover each root's dependency tree sequentially so
        // setDiscoveredSoFar flushes between roots and the user sees
        // the count increment in the modal.
        // eslint-disable-next-line no-restricted-syntax -- Sequential discovery for live progress updates
        for (const rootEntity of roots) {
          // Fresh-GET root before traversal to ensure up-to-date state
          // eslint-disable-next-line no-await-in-loop -- Intentional: sequential for live UI updates
          const freshRootResponse = await fetchEntityOnSourceById(
            rootEntity.id,
            rootEntity.attributes?.entityMetadata?.domainEntityType,
          );
          const freshRootData = freshRootResponse?.data;
          const freshRoot = isConfigEntityDetail(freshRootData)
            ? freshRootData
            : rootEntity;

          const rootDomainEntityType =
            freshRoot.attributes?.entityMetadata?.domainEntityType ??
            domainEntity.type;
          const rootSchema = domainEntity.schema;

          // eslint-disable-next-line no-await-in-loop -- Intentional: sequential for live UI updates
          const result = await getAllReferencedEntities({
            entityData: freshRoot,
            entitySchema: rootSchema,
            loadEntityRelationshipsSchemas,
            findEntityOnSourceByDomainEntityId,
            fetchEntityOnSourceById,
          });

          const {
            entities: entitiesMap,
            failures: discoveryFailures,
            schemas: discoveredSchemas,
          } = result;

          // Update live count — sequential await lets React flush this
          totalDiscoveredEntities += entitiesMap.size;
          setDiscoveredSoFar(totalDiscoveredEntities);

          // Merge schemas
          discoveredSchemas.set(rootDomainEntityType, rootSchema);
          discoveredSchemas.forEach((schemaValue, key) => {
            unifiedSchemas.set(key, schemaValue);
          });

          // Merge entities into unified map (dedup by Map key)
          entitiesMap.forEach((value, key) => {
            unifiedMap.set(key, value);
          });

          // Build tree entries for this root
          const entries: DeepPublishTreeEntry[] = Array.from(
            entitiesMap.values(),
          ).map(({ entity: discoveredEntity, dependencies }) => {
            const metadata = getEntityMetadata(discoveredEntity);
            const link = getEntityDetailPath?.(
              metadata.domainEntityType,
              discoveredEntity.id,
            );
            return {
              entity: discoveredEntity,
              dependencies,
              status: metadata.status,
              domainEntityType: metadata.domainEntityType,
              entityName: metadata.entityName,
              link,
            };
          });

          // Mark shared dependencies as "already included" on later
          // tree occurrences for UX display. Entries whose entity ID
          // was already claimed by a previous root get an `alreadyIncluded`
          // flag in the tree entry list. The first root to claim an
          // entity "owns" it for display purposes.
          const markedEntries = entries.map((entry) => {
            const alreadyIncluded = claimedEntityIds.has(entry.entity.id);
            if (!alreadyIncluded) {
              claimedEntityIds.add(entry.entity.id);
            }
            return { ...entry, alreadyIncluded };
          });

          if (discoveryFailures.length > 0) {
            anyFailures = true;
          }

          perRootTrees.push({
            rootEntity: freshRoot,
            treeEntries: markedEntries,
            discoveryFailures,
          });

          if (isProduction() && discoveryFailures.length > 0) {
            captureError(
              new Error(
                `Bulk deep publish: ${discoveryFailures.length} discovery failures for root ${freshRoot.id}`,
              ),
              {
                errorName: 'BulkDeepPublishDiscoveryFailures',
                context: {
                  BulkDeepPublishDiscovery: {
                    rootEntityId: freshRoot.id,
                    failureCount: String(discoveryFailures.length),
                  },
                },
              },
            );
          }
        }

        unifiedEntitiesMapRef.current = unifiedMap;
        unifiedSchemasMapRef.current = unifiedSchemas;

        // Calculate totals
        const draftCount = Array.from(unifiedMap.values()).filter(
          ({ entity }) =>
            entity.attributes?.entityMetadata?.status !==
            EntityStatusValue.Published,
        ).length;

        // Dedup count = total across individual trees minus unique entities
        const deduped = totalDiscoveredEntities - unifiedMap.size;

        setRootTrees(perRootTrees);
        setTotalEntityCount(unifiedMap.size);
        setTotalDraftCount(draftCount);
        setDeduplicatedCount(deduped);
        setHasDiscoveryFailures(anyFailures);
      } catch (error) {
        setIsDiscoveryError(true);
        const message =
          error instanceof Error ? error.message : 'Unknown error';
        setDiscoveryErrorMessage(message);

        if (isProduction()) {
          captureError(
            error instanceof Error ? error : new Error(String(error)),
            { errorName: 'BulkDeepPublishDiscoveryError' },
          );
        }
      } finally {
        setIsDiscovering(false);
      }
    },
    [
      domainEntity,
      loadEntityRelationshipsSchemas,
      findEntityOnSourceByDomainEntityId,
      fetchEntityOnSourceById,
      getEntityDetailPath,
    ],
  );

  const executeBulkDeepPublish = useCallback(async () => {
    const entitiesMap = unifiedEntitiesMapRef.current;
    if (entitiesMap.size === 0) return;

    setIsExecuting(true);
    const abortController = createAbortController();

    const { failedEntityIds } = await executeDeepPublishLoop({
      entitiesMap,
      schemasMap: unifiedSchemasMapRef.current,
      previouslyFailedIds: failedEntityIdsRef.current,
      configEntity,
      baseConfigEntitySchema: configEntityConfig.schema,
      domainEntityType: domainEntity.type,
      environment,
      environmentFetch,
      fetchEntityOnSourceById,
      userInfo,
      formatMessage,
      bulkActionReportingStore,
      abortSignal: abortController.signal,
    });

    if (abortController.signal.aborted) {
      setIsExecuting(false);
      return;
    }

    failedEntityIdsRef.current = failedEntityIds;

    queryClient.invalidateQueries({
      queryKey: [environment],
    });

    setIsExecuting(false);
  }, [
    configEntity,
    configEntityConfig.schema,
    domainEntity.type,
    environment,
    environmentFetch,
    bulkActionReportingStore,
    createAbortController,
    fetchEntityOnSourceById,
    formatMessage,
    userInfo,
    queryClient,
  ]);

  const cancelPublish = useCallback(() => {
    abort();
    const store = bulkActionReportingStore.getState();
    if (store.session?.status === 'running') {
      store.completeSession();
    }
    setIsExecuting(false);
  }, [abort, bulkActionReportingStore]);

  return {
    rootTrees,
    totalEntityCount,
    totalDraftCount,
    deduplicatedCount,
    hasDiscoveryFailures,
    isDiscovering,
    discoveredSoFar,
    isDiscoveryError,
    discoveryErrorMessage,
    isExecuting,
    discoverTrees,
    executeBulkDeepPublish,
    cancelPublish,
    reset,
  };
};
